import React, { useCallback, useContext, useMemo, useState } from "react";
import { Platform, View } from "react-native";
import styled from "styled-components/native";
import QKModal, { MODAL_CLOSE_DELAY } from "@app/components/modal";
import { Analytics } from "@app/analytics";
import QKScrollView from "@app/components/questkit/ScrollView";
import Button from "@app/components/questkit/button";
import HeaderText from "@app/components/questkit/headerText";
import Text from "@app/components/questkit/text";
import TextInput from "@app/components/questkit/textInput";
import Switch from "@app/components/questkit/switch";
import ListItem from "@app/components/questkit/listItem";
import { DateTimePicker } from "@app/components/questkit/dateTimePicker";
import {
  StandardDropdownListItem,
  StandardInlineDropdown,
} from "@app/components/questkit/dropdown/StandardInlineDropdown";
import { createQuestmateApiClient } from "@app/util/client";
import {
  SnackbarContext,
  SnackbarSeverity,
} from "@app/components/snackbar/SnackbarContext";
import { subDays, subMonths, startOfDay, formatISO } from "date-fns";
import { useAppSelector } from "@app/store";
import { selectLoggedInUser } from "@app/store/cache/users";
import { updateMe } from "@app/util/client/requests/me";

type TimeRangeValue =
  | "all"
  | "last7days"
  | "last30days"
  | "last90days"
  | "last6months"
  | "lastyear"
  | "custom";

const TIME_RANGE_OPTIONS: StandardDropdownListItem<TimeRangeValue>[] = [
  { value: "all", label: "All time" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "last90days", label: "Last 90 days" },
  { value: "last6months", label: "Last 6 months" },
  { value: "lastyear", label: "Last year" },
  { value: "custom", label: "Custom date range" },
];

type ExportFormat = "csv" | "pdf";

const FORMAT_OPTIONS: StandardDropdownListItem<ExportFormat>[] = [
  { value: "pdf", label: "PDF (One page per run)" },
  { value: "csv", label: "CSV (Spreadsheet)" },
];

type PageSize = "letter" | "a4";

const PAGE_SIZE_OPTIONS: StandardDropdownListItem<PageSize>[] = [
  { value: "letter", label: "Letter (8.5 x 11 in)" },
  { value: "a4", label: "A4 (210 x 297 mm)" },
];

interface ExportRunsModalProps {
  questId: string;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

export const ExportRunsModal: React.FC<ExportRunsModalProps> = ({
  questId,
  showModal,
  setShowModal,
}) => {
  return (
    <QKModal
      testID="export-runs-modal"
      showModal={showModal}
      setShowModal={setShowModal}
      title="Export Runs"
    >
      <ExportRunsModalContent
        questId={questId}
        onClose={() => setShowModal(false)}
      />
    </QKModal>
  );
};

interface ExportRunsModalContentProps {
  questId: string;
  onClose: () => void;
}

const ExportRunsModalContent: React.FC<ExportRunsModalContentProps> = ({
  questId,
  onClose,
}) => {
  const snackbar = useContext(SnackbarContext);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const loggedInUser = useAppSelector(selectLoggedInUser);
  const userDefaultPageSize =
    (loggedInUser?.defaultPdfPageSize as PageSize) || "letter";

  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const [pageSize, setPageSize] = useState<PageSize>(userDefaultPageSize);
  const [timeRange, setTimeRange] = useState<TimeRangeValue>("all");
  const [customStartDate, setCustomStartDate] = useState<string | null>(null);
  const [customEndDate, setCustomEndDate] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [limitText, setLimitText] = useState("");

  const exportParams = useMemo(() => {
    let startDate: string | null = null;
    let endDate: string | null = null;
    const now = new Date();

    switch (timeRange) {
      case "last7days":
        startDate = formatISO(startOfDay(subDays(now, 7)));
        break;
      case "last30days":
        startDate = formatISO(startOfDay(subDays(now, 30)));
        break;
      case "last90days":
        startDate = formatISO(startOfDay(subDays(now, 90)));
        break;
      case "last6months":
        startDate = formatISO(startOfDay(subMonths(now, 6)));
        break;
      case "lastyear":
        startDate = formatISO(startOfDay(subMonths(now, 12)));
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
    }

    const limit = limitText ? parseInt(limitText, 10) : null;

    return {
      startDate,
      endDate,
      includeArchived,
      limit: limit && !isNaN(limit) && limit > 0 ? limit : null,
    };
  }, [timeRange, customStartDate, customEndDate, includeArchived, limitText]);

  const onExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const client = createQuestmateApiClient();
      const params = new URLSearchParams();

      if (exportParams.startDate) {
        params.append("startDate", exportParams.startDate);
      }
      if (exportParams.endDate) {
        params.append("endDate", exportParams.endDate);
      }
      if (exportParams.includeArchived) {
        params.append("archived", "include");
      }
      if (exportParams.limit) {
        params.append("limit", exportParams.limit.toString());
      }
      if (exportFormat === "pdf") {
        params.append("pageSize", pageSize);
      }

      const queryString = params.toString();
      const exportPath =
        exportFormat === "pdf" ? "/runs/export/pdf" : "/runs/export";
      const url = `/quests/${questId}${exportPath}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await client.get(url, {
        responseType: "blob",
      });

      // Trigger download
      if (Platform.OS === "web") {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/);
        const extension = exportFormat === "pdf" ? "pdf" : "csv";
        const filename =
          filenameMatch?.[1] ||
          `quest_runs_export_${
            new Date().toISOString().split("T")[0]
          }.${extension}`;

        const blobUrl = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
      }

      setExportSuccess(true);
      Analytics.trackEvent("Export Quest Runs", { format: exportFormat });
      snackbar.sendMessage(
        "Export downloaded successfully",
        SnackbarSeverity.NOTICE
      );
      setTimeout(() => {
        onClose();
      }, MODAL_CLOSE_DELAY);
    } catch (error) {
      console.error("Export failed:", error);
      snackbar.sendMessage("Failed to export runs", SnackbarSeverity.WARNING);
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    questId,
    exportFormat,
    pageSize,
    exportParams,
    snackbar,
    onClose,
  ]);

  const preventUserInput = isExporting || exportSuccess;

  return (
    <OptionsDialogWrapper>
      <StyledScrollView keyboardShouldPersistTaps="always">
        <View onStartShouldSetResponder={() => true}>
          <OptionGroup>
            <HeaderText title="Format" />
            <StandardInlineDropdown
              value={exportFormat}
              options={FORMAT_OPTIONS}
              optionNoun="format"
              optionPluralNoun="formats"
              loadingOptions={false}
              onSelect={(selection) =>
                selection && setExportFormat(selection.value)
              }
              readOnly={preventUserInput}
            />
            {exportFormat === "pdf" && (
              <StandardInlineDropdown
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                optionNoun="page size"
                optionPluralNoun="page sizes"
                loadingOptions={false}
                onSelect={(selection) => {
                  if (selection) {
                    setPageSize(selection.value);
                    // Fire and forget - don't block UI for preference save
                    updateMe({ defaultPdfPageSize: selection.value }).catch(
                      (err) => {
                        console.warn(
                          "Failed to save page size preference:",
                          err
                        );
                      }
                    );
                  }
                }}
                readOnly={preventUserInput}
              />
            )}
          </OptionGroup>

          <OptionGroup>
            <HeaderText title="Time Range" />
            <StandardInlineDropdown
              value={timeRange}
              options={TIME_RANGE_OPTIONS}
              optionNoun="time range"
              optionPluralNoun="time ranges"
              loadingOptions={false}
              onSelect={(selection) =>
                selection && setTimeRange(selection.value)
              }
              readOnly={preventUserInput}
            />
            {timeRange === "custom" && (
              <CustomDateContainer>
                <DateTimePicker
                  title="Start Date & Time"
                  value={customStartDate || ""}
                  onDone={setCustomStartDate}
                  time={true}
                  readOnly={preventUserInput}
                />
                <DateTimePicker
                  title="End Date & Time"
                  value={customEndDate || ""}
                  onDone={setCustomEndDate}
                  time={true}
                  readOnly={preventUserInput}
                />
              </CustomDateContainer>
            )}
          </OptionGroup>

          <OptionGroup>
            <HeaderText title="Options" />
            <ListItem
              text="Include archived runs"
              actionComponent={
                <Switch
                  switchOn={includeArchived}
                  onSwitch={() => setIncludeArchived(!includeArchived)}
                  locked={preventUserInput}
                />
              }
            />
            <LimitRow>
              <LimitLabel>Limit results to</LimitLabel>
              <LimitInput
                value={limitText}
                onChangeText={(text) =>
                  setLimitText(text.replace(/[^0-9]/g, ""))
                }
                placeholder="All"
                keyboardType="number-pad"
                editable={!preventUserInput}
              />
            </LimitRow>
          </OptionGroup>
        </View>
      </StyledScrollView>
      <ButtonContainer>
        <StyledButton
          title={`Export ${exportFormat.toUpperCase()}`}
          onPress={onExport}
          loading={isExporting}
          success={exportSuccess}
        />
      </ButtonContainer>
    </OptionsDialogWrapper>
  );
};

const OptionsDialogWrapper = styled.View`
  padding-vertical: 10px;
  flex-shrink: 1;
`;

const StyledScrollView = styled(QKScrollView)`
  padding-horizontal: 20px;
`;

const OptionGroup = styled.View`
  margin-bottom: 24px;
`;

const CustomDateContainer = styled.View`
  gap: 8px;
`;

const LimitRow = styled.View`
  flex-direction: row;
  align-items: center;
  padding-vertical: 12px;
`;

const LimitLabel = styled(Text)`
  flex: 1;
`;

const LimitInput = styled(TextInput)`
  width: 120px;
`;

const ButtonContainer = styled.View`
  padding-horizontal: 20px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  align-self: center;
  margin-top: 20px;
  margin-bottom: 10px;
`;
